import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Clock, 
  Calendar, 
  MapPin, 
  Briefcase, 
  Save, 
  Plus,
  CheckCircle,
  AlertCircle,
  Wrench,
  Package,
  Sun,
  CloudRain,
  CloudSnow
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileTimesheet = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myProjects, setMyProjects] = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  
  const [entryData, setEntryData] = useState({
    project_id: '',
    work_date: new Date().toISOString().split('T')[0],
    start_time: '07:00',
    end_time: '15:30',
    break_minutes: 30,
    regular_hours: 8.0,
    overtime_hours: 0.0,
    double_time_hours: 0.0,
    work_description: '',
    location: '',
    weather_conditions: 'clear',
    equipment_used: [],
    materials_used: [],
    notes: ''
  });

  const weatherOptions = [
    { value: 'clear', label: 'Clear/Sunny', icon: Sun },
    { value: 'cloudy', label: 'Cloudy', icon: CloudRain },
    { value: 'rainy', label: 'Rainy', icon: CloudRain },
    { value: 'snowy', label: 'Snow', icon: CloudSnow },
    { value: 'windy', label: 'Windy', icon: Sun },
    { value: 'hot', label: 'Hot', icon: Sun },
    { value: 'cold', label: 'Cold', icon: CloudSnow }
  ];

  const commonEquipment = [
    'Crane', 'Forklift', 'Welder', 'Grinder', 'Drill', 'Impact Gun', 
    'Torque Wrench', 'Level', 'Measuring Tape', 'Safety Harness'
  ];

  const commonMaterials = [
    'Steel Beams', 'Bolts', 'Welding Rods', 'Cutting Discs', 
    'Safety Wire', 'Lubricants', 'Gaskets', 'Bearings'
  ];

  useEffect(() => {
    if (user?.role === 'field_employee') {
      fetchMyProjects();
      fetchMyEntries();
    }
  }, [user]);

  const fetchMyProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setMyProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load your assigned projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyEntries = async () => {
    try {
      // Get entries for the current week
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay()); // Sunday
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Saturday

      const response = await axios.get(`${API}/timesheets/my-entries`, {
        params: {
          start_date: weekStart.toISOString().split('T')[0],
          end_date: weekEnd.toISOString().split('T')[0]
        }
      });
      setMyEntries(response.data);
    } catch (error) {
      console.error('Error fetching timesheet entries:', error);
    }
  };

  const calculateHoursFromTimes = () => {
    if (entryData.start_time && entryData.end_time) {
      const [startHour, startMin] = entryData.start_time.split(':').map(Number);
      const [endHour, endMin] = entryData.end_time.split(':').map(Number);
      
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      
      let totalMinutes = endMinutes - startMinutes;
      if (totalMinutes < 0) totalMinutes += 24 * 60; // Handle overnight
      
      totalMinutes -= entryData.break_minutes;
      const totalHours = totalMinutes / 60;
      
      // Standard calculation: up to 8 hours regular, next 4 hours OT, rest double time
      let regular = Math.min(totalHours, 8);
      let overtime = Math.min(Math.max(totalHours - 8, 0), 4);
      let doubleTime = Math.max(totalHours - 12, 0);
      
      setEntryData({
        ...entryData,
        regular_hours: Number(regular.toFixed(2)),
        overtime_hours: Number(overtime.toFixed(2)),
        double_time_hours: Number(doubleTime.toFixed(2))
      });
    }
  };

  const handleSubmit = async () => {
    if (!entryData.project_id) {
      toast.error('Please select a project');
      return;
    }

    if (!entryData.work_description.trim()) {
      toast.error('Please describe the work performed');
      return;
    }

    setSaving(true);
    
    try {
      await axios.post(`${API}/timesheets`, entryData);
      toast.success('Timesheet entry saved successfully!');
      
      // Reset form
      setEntryData({
        ...entryData,
        work_description: '',
        location: '',
        equipment_used: [],
        materials_used: [],
        notes: ''
      });
      
      // Refresh entries
      fetchMyEntries();
      
    } catch (error) {
      console.error('Error saving timesheet:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to save timesheet entry');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleEquipment = (equipment) => {
    const current = entryData.equipment_used;
    if (current.includes(equipment)) {
      setEntryData({
        ...entryData,
        equipment_used: current.filter(e => e !== equipment)
      });
    } else {
      setEntryData({
        ...entryData,
        equipment_used: [...current, equipment]
      });
    }
  };

  const toggleMaterial = (material) => {
    const current = entryData.materials_used;
    if (current.includes(material)) {
      setEntryData({
        ...entryData,
        materials_used: current.filter(m => m !== material)
      });
    } else {
      setEntryData({
        ...entryData,
        materials_used: [...current, material]
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTodaysEntry = () => {
    const today = new Date().toISOString().split('T')[0];
    return myEntries.find(entry => entry.work_date === today);
  };

  const todaysEntry = getTodaysEntry();

  if (user?.role !== 'field_employee') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="h-12 w-12 text-slate-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Field Employee Access Only</h2>
          <p className="text-slate-500">This timesheet is for field employees</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Daily Timesheet</h1>
        <p className="text-slate-500">Log your work hours and activities</p>
        <div className="mt-3 text-sm text-blue-700">
          Welcome back, {user?.full_name}
        </div>
      </div>

      {/* Today's Status */}
      {todaysEntry ? (
        <Card className="card mb-6 border-green-200 bg-green-50">
          <CardContent className="card-body">
            <div className="flex items-center justify-center text-green-700">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>Today's timesheet completed - {todaysEntry.total_hours} hours logged</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="card mb-6 border-orange-200 bg-orange-50">
          <CardContent className="card-body">
            <div className="flex items-center justify-center text-orange-700">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>No timesheet entry for today yet</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Entry Form */}
      <Card className="card mb-6">
        <CardHeader className="card-header">
          <CardTitle className="text-slate-900 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-blue-500" />
            Log Today's Work
          </CardTitle>
        </CardHeader>
        <CardContent className="card-body space-y-4">
          {/* Project Selection */}
          <div>
            <Label className="text-slate-700">Select Project *</Label>
            <select
              value={entryData.project_id}
              onChange={(e) => setEntryData({...entryData, project_id: e.target.value})}
              className="w-full px-3 py-3 bg-slate-100 border border-slate-300 rounded-md text-slate-900 text-lg"
            >
              <option value="">Choose your project...</option>
              {myProjects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.title} - {project.client_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date and Times */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-slate-700">Work Date</Label>
              <Input
                type="date"
                value={entryData.work_date}
                onChange={(e) => setEntryData({...entryData, work_date: e.target.value})}
                className="bg-slate-100 border-slate-300 text-slate-900 text-lg py-3"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-slate-700 text-sm">Start Time</Label>
                <Input
                  type="time"
                  value={entryData.start_time}
                  onChange={(e) => setEntryData({...entryData, start_time: e.target.value})}
                  onBlur={calculateHoursFromTimes}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 text-sm">End Time</Label>
                <Input
                  type="time"
                  value={entryData.end_time}
                  onChange={(e) => setEntryData({...entryData, end_time: e.target.value})}
                  onBlur={calculateHoursFromTimes}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 text-sm">Break (min)</Label>
                <Input
                  type="number"
                  value={entryData.break_minutes}
                  onChange={(e) => setEntryData({...entryData, break_minutes: parseInt(e.target.value) || 0})}
                  onBlur={calculateHoursFromTimes}
                  className="bg-slate-100 border-slate-300 text-slate-900"
                />
              </div>
            </div>

            <Button
              onClick={calculateHoursFromTimes}
              variant="outline"
              size="sm"
              className="border-blue-500 text-blue-700 hover:bg-blue-900"
            >
              Calculate Hours from Times
            </Button>
          </div>

          {/* Hours Breakdown */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-slate-700 text-sm">Regular</Label>
              <Input
                type="number"
                step="0.25"
                value={entryData.regular_hours}
                onChange={(e) => setEntryData({...entryData, regular_hours: parseFloat(e.target.value) || 0})}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 text-sm">Overtime</Label>
              <Input
                type="number"
                step="0.25"
                value={entryData.overtime_hours}
                onChange={(e) => setEntryData({...entryData, overtime_hours: parseFloat(e.target.value) || 0})}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 text-sm">Double Time</Label>
              <Input
                type="number"
                step="0.25"
                value={entryData.double_time_hours}
                onChange={(e) => setEntryData({...entryData, double_time_hours: parseFloat(e.target.value) || 0})}
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>
          </div>

          {/* Work Description */}
          <div>
            <Label className="text-slate-700">Work Description *</Label>
            <Textarea
              value={entryData.work_description}
              onChange={(e) => setEntryData({...entryData, work_description: e.target.value})}
              placeholder="Describe the work you performed today..."
              className="bg-slate-100 border-slate-300 text-slate-900"
              rows={3}
            />
          </div>

          {/* Location & Weather */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700">Work Location</Label>
              <Input
                value={entryData.location}
                onChange={(e) => setEntryData({...entryData, location: e.target.value})}
                placeholder="Building A, 2nd Floor..."
                className="bg-slate-100 border-slate-300 text-slate-900"
              />
            </div>
            
            <div>
              <Label className="text-slate-700">Weather</Label>
              <select
                value={entryData.weather_conditions}
                onChange={(e) => setEntryData({...entryData, weather_conditions: e.target.value})}
                className="w-full px-3 py-2 bg-slate-100 border border-slate-300 rounded-md text-slate-900"
              >
                {weatherOptions.map(weather => (
                  <option key={weather.value} value={weather.value}>
                    {weather.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Equipment Used */}
          <div>
            <Label className="text-slate-700 mb-2 block">Equipment Used (tap to select)</Label>
            <div className="flex flex-wrap gap-2">
              {commonEquipment.map(equipment => (
                <button
                  key={equipment}
                  type="button"
                  onClick={() => toggleEquipment(equipment)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    entryData.equipment_used.includes(equipment)
                      ? 'bg-blue-600 border-blue-500 text-white'
                      : 'bg-slate-100 border-slate-300 text-slate-700 hover:border-blue-500'
                  }`}
                >
                  <Wrench className="h-3 w-3 inline mr-1" />
                  {equipment}
                </button>
              ))}
            </div>
          </div>

          {/* Materials Used */}
          <div>
            <Label className="text-slate-700 mb-2 block">Materials Used (tap to select)</Label>
            <div className="flex flex-wrap gap-2">
              {commonMaterials.map(material => (
                <button
                  key={material}
                  type="button"
                  onClick={() => toggleMaterial(material)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    entryData.materials_used.includes(material)
                      ? 'bg-green-600 border-green-500 text-white'
                      : 'bg-slate-100 border-slate-300 text-slate-700 hover:border-green-500'
                  }`}
                >
                  <Package className="h-3 w-3 inline mr-1" />
                  {material}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-slate-700">Additional Notes</Label>
            <Textarea
              value={entryData.notes}
              onChange={(e) => setEntryData({...entryData, notes: e.target.value})}
              placeholder="Any additional notes, issues, or observations..."
              className="bg-slate-100 border-slate-300 text-slate-900"
              rows={2}
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={saving || !entryData.project_id || !entryData.work_description.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
          >
            {saving ? (
              <>
                <Clock className="h-5 w-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Save Timesheet Entry
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Recent Entries */}
      <Card className="card">
        <CardHeader className="card-header">
          <CardTitle className="text-slate-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-blue-500" />
            This Week's Entries
          </CardTitle>
        </CardHeader>
        <CardContent className="card-body">
          {myEntries.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">No timesheet entries this week</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myEntries.map((entry) => (
                <div key={entry.id} className="p-3 bg-slate-100 rounded-lg border border-slate-300">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium text-slate-900">{formatDate(entry.work_date)}</h4>
                      <p className="text-sm text-slate-500">{entry.project_title}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-green-700 font-mono text-sm">
                        {entry.total_hours}h
                      </div>
                      {entry.supervisor_approved && (
                        <div className="text-xs text-green-700">
                          <CheckCircle className="h-3 w-3 inline mr-1" />
                          Approved
                        </div>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-slate-700 mb-2">{entry.work_description}</p>
                  <div className="text-xs text-slate-500">
                    R: {entry.regular_hours}h | OT: {entry.overtime_hours}h | DT: {entry.double_time_hours}h
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MobileTimesheet;